import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { forkJoin, of } from 'rxjs'
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators'
import {
  AddTask,
  AddTaskComplete,
  AddTasks,
  AddTasksComplete,
  ChangeTaskStatus,
  ChangeTaskStatusComplete,
  DeleteTask,
  DeleteTaskComplete,
  LoadTasks,
  LoadTasksComplete,
  UpdateTask,
  UpdateTaskComplete,
} from './task.actions'
import { TaskService } from './task.service'
import * as moment from 'moment'
import { TaskRequest } from '@tv3/interfaces/requests/task-request'
import * as R from 'ramda'
import { Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { selectInquiries } from '@tv3/store/inquiry/inquiry.selectors'
import { Task } from './task.model'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { FetchInquiriesComplete } from '@tv3/store/inquiry/inquiry.actions'
import { selectOnce } from '@tokeet/tv3-platform'
import { ActionFailed } from '@tokeet-frontend/utility'

@Injectable()
export class TaskEffects {
  @Effect()
  loadTasks$ = this.actions$.pipe(
    ofType(LoadTasks),
    switchMap(() =>
      this.tasks.all().pipe(
        concatMap((tasks) =>
          this.store.pipe(
            selectOnce(selectInquiries),
            map((inquiries) => {
              /**
               * we check whether we already have some inquiries in the store
               * the ones we don't have we request from the API
               */
              const inquiryIds = R.map(R.prop('id'), inquiries)
              const missingInquiryIds = R.pipe(
                R.reduce((acc: string[], task: Task) => {
                  if (!R.contains(task.inquiryId, inquiryIds)) {
                    return R.append(task.inquiryId, acc)
                  }
                  return acc
                }, []),
                R.reject(R.isEmpty),
                R.uniq
              )(tasks)

              return {
                missingInquiryIds,
                tasks,
              }
            })
          )
        ),
        concatMap(({ missingInquiryIds, tasks }) =>
          this.inquiryService.get(missingInquiryIds).pipe(map((inquiries) => ({ inquiries, tasks })))
        ),
        switchMap(({ inquiries, tasks }) => [LoadTasksComplete({ tasks }), FetchInquiriesComplete({ inquiries })]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addTask$ = this.actions$.pipe(
    ofType(AddTask),
    map(({ form }) => {
      return {
        name: form.name,
        due: moment
          .utc(
            `${(form.dueDate as unknown as moment.Moment).format('DD - MM - YYYY')} ${form.dueTime}`,
            `DD - MM - YYYY HH:mm`
          )
          .unix(),
        list: form.taskList,
        user_id: form.user,
        inquiry_id: R.defaultTo('', form.inquiryId),
        rental_id: R.defaultTo('', form.rentalId),
        notes: R.defaultTo('', form.note),
        status: 1,
      } as TaskRequest
    }),
    concatMap((request) => {
      return this.tasks.add(request).pipe(
        map((task) => AddTaskComplete({ task })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  addTasks$ = this.actions$.pipe(
    ofType(AddTasks),
    map(({ requests }) =>
      requests.map((item) =>
        this.tasks.add(item, requests[requests.length - 1] !== item, 'Tasks created successfully.')
      )
    ),
    concatMap((list) =>
      forkJoin(list).pipe(
        map((tasks: Task[]) => AddTasksComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  updateTask$ = this.actions$.pipe(
    ofType(UpdateTask),
    map(({ form, task }) => ({
      request: {
        name: form.name,
        due: moment
          .utc(`${moment(form.dueDate).format('DD - MMM - YYYY')} ${form.dueTime}`, `DD - MMM - YYYY HH:mm`)
          .unix(),
        list: form.taskList,
        user_id: form.user,
        inquiry_id: R.defaultTo('', form.inquiryId),
        rental_id: R.defaultTo('', form.rentalId),
        notes: R.defaultTo('', form.note),
        status: 1,
      } as TaskRequest,
      task,
    })),
    switchMap((payload) =>
      this.tasks.update(payload).pipe(
        map((task) => UpdateTaskComplete({ update: { id: task.id, changes: task } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteTask$ = this.actions$.pipe(
    ofType(DeleteTask),
    mergeMap(({ id }) =>
      this.tasks.delete(id).pipe(
        map((task) => DeleteTaskComplete({ id: task.id })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  changeTaskStatus$ = this.actions$.pipe(
    ofType(ChangeTaskStatus),
    mergeMap((payload) =>
      this.tasks.changeStatus(payload).pipe(
        map((task) => ChangeTaskStatusComplete({ update: { id: task.id, changes: task } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(
    private actions$: Actions,
    private store: Store<fromRoot.State>,
    private inquiryService: InquiryService,
    private tasks: TaskService
  ) {}
}
